const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export interface CompanyListItem {
  id: number;
  name: string;
  profile_picture: number | null;
  website: string | null;
  email: string | null;
  contact_number: string | null;
  industry: { id: number; name: string } | null;
  country: { id: number; name: string } | null;
  city: { id: number; name: string } | null;
  address_line_1: string | null;
  address_line_2: string | null;
  about: string | null;
}

export interface CompanyListResponse {
  message?: string;
  code?: number;
  data?: CompanyListItem[] | CompanyListItem;
  error?: Record<string, unknown>;
  pagination?: {
    count: number;
    total_pages: number;
    current_page: number;
  };
  [key: string]: unknown;
}

export async function fetchCompanyList(
  accessToken: string
): Promise<{ data?: CompanyListItem[]; error?: string; pagination?: { count: number; total_pages: number; current_page: number } }> {
  const endpoint = `${API_BASE_URL}/api/company/company/list/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch company list.' };
  }
  
  // Handle different response structures
  let companiesData: CompanyListItem[] = [];
  let paginationData: { count: number; total_pages: number; current_page: number } | undefined;

  if (responseBody) {
    // Check if response has results array (pagination format)
    if (Array.isArray(responseBody.results)) {
      companiesData = responseBody.results as CompanyListItem[];
      paginationData = responseBody.pagination;
    } 
    // Check if response has data array
    else if (Array.isArray(responseBody.data)) {
      companiesData = responseBody.data as CompanyListItem[];
      paginationData = responseBody.pagination;
    }
    // Check if response is directly an array
    else if (Array.isArray(responseBody)) {
      companiesData = responseBody as CompanyListItem[];
    }
    // Check if response has nested data structure
    else if (responseBody.data && Array.isArray(responseBody.data)) {
      companiesData = responseBody.data as CompanyListItem[];
      paginationData = responseBody.pagination;
    }
  }

  // Default pagination if not provided
  const pagination = paginationData || {
    count: companiesData.length,
    total_pages: 1,
    current_page: 1
  };
  
  return { 
    data: companiesData,
    pagination
  };
}

export interface CreateCompanyPayload {
  name: string;
  website?: string | null;
  email?: string | null;
  contact_number?: string | null;
  industry?: number | null;
  country?: number | null;
  city?: number | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  about?: string | null;
  profile_picture?: number | null;
}

/** Format API error object (e.g. { website: ["Enter a valid URL."] }) into a single string for toast */
function formatCompanyErrorResponse(
  message?: string,
  errorObj?: Record<string, unknown>
): string {
  const parts: string[] = [];
  if (message && String(message).trim()) parts.push(String(message).trim());
  if (errorObj && typeof errorObj === "object") {
    const fieldMessages = Object.entries(errorObj)
      .filter(([, v]) => Array.isArray(v) && v.length > 0)
      .map(([field, v]) => `${field}: ${(v as string[]).join(", ")}`);
    if (fieldMessages.length > 0) {
      parts.push(fieldMessages.join(". "));
    }
  }
  return parts.join(". ") || "Something went wrong.";
}

export interface CompanyResponse {
  message?: string;
  code?: number;
  data?: {
    id: number;
    name: string;
    profile_picture: number | null;
    website: string | null;
    email: string | null;
    contact_number: string | null;
    industry: number | null;
    country: number | null;
    city: number | null;
    address_line_1: string | null;
    address_line_2: string | null;
    about: string | null;
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export async function createCompany(
  payload: CreateCompanyPayload,
  accessToken: string
): Promise<{ data?: CompanyResponse['data']; error?: string }> {
  const endpoint = `${API_BASE_URL}/api/company/company/create/`;

  try {
    const { authenticatedFetch } = await import("../auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(payload),
    });

    const responseBody: CompanyResponse = await response.json().catch(() => null);
    if (!response.ok) {
      const errorMessage = formatCompanyErrorResponse(
        responseBody?.message as string | undefined,
        responseBody?.error as Record<string, unknown> | undefined
      );
      return { error: errorMessage || "Failed to create company." };
    }
    return { data: responseBody.data };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to create company.",
    };
  }
}

export async function updateCompany(
  companyId: number,
  payload: CreateCompanyPayload,
  accessToken: string
): Promise<{ data?: CompanyResponse['data']; error?: string }> {
  const endpoint = `${API_BASE_URL}/api/company/company/${companyId}/`;

  try {
    const { authenticatedFetch } = await import("../auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "PUT",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(payload),
    });

    const responseBody: CompanyResponse = await response.json().catch(() => null);
    if (!response.ok) {
      const errorMessage = formatCompanyErrorResponse(
        responseBody?.message as string | undefined,
        responseBody?.error as Record<string, unknown> | undefined
      );
      return { error: errorMessage || "Failed to update company." };
    }
    return { data: responseBody.data };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Failed to update company.",
    };
  }
}

export async function updateCompanyDepartments(
  companyId: number,
  accessToken: string
): Promise<{ success: boolean; error?: string }> {
  const endpoint = `${API_BASE_URL}/api/company/department/${companyId}/`;

  try {
    const { authenticatedFetch } = await import("../auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "PUT",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const responseBody = await response.json().catch(() => null);
      return {
        success: false,
        error: (responseBody && responseBody.message) || 'Failed to update company departments.'
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to update company departments.",
    };
  }
}

export async function deleteCompany(
  companyId: number,
  accessToken: string
): Promise<{ success: boolean; error?: string }> {
  const endpoint = `${API_BASE_URL}/api/company/company/${companyId}/`;

  try {
    const { authenticatedFetch } = await import("../auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
    });

    if (!response.ok) {
      const responseBody = await response.json().catch(() => null);
      return {
        success: false,
        error: (responseBody && responseBody.message) || 'Failed to delete company.'
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to delete company.",
    };
  }
}

